import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Banner } from '@/_shared/components/Banner';
import { openTabForAsyncRedirect } from '@/_shared/helpers/openTabForAsyncRedirect';
import { useLayoutBanner } from '@/_shared/hooks/useLayoutBanner';
import { getBillingLinkCall } from '@/billing/state/api/getBillingLinkCall';
import { getUserWarningsCall } from '@/core/state/api/getUserWarningsCall';

const BANNER_KEY = 'banner.trialExpiring';

export const TrialExpiringBanner: FC = () => {
  const t = useTranslations('billing.trialExpiringBanner');
  const { setBanner } = useLayoutBanner();
  const { isTrialExpiring } = getUserWarningsCall.hook();
  const { execute: executeGetBillingLink } = getBillingLinkCall.hook();
  const [isDismissed, setIsDismissed] = useState(false);

  const onCtaClick = useCallback(() => {
    const { redirect, close } = openTabForAsyncRedirect();

    executeGetBillingLink({
      successSaga: ({ response }) => {
        if (response?.link) {
          redirect(response.link);
        } else {
          close();
        }
      },
      errorSaga: () => {
        close();
      },
    });
  }, [executeGetBillingLink]);

  const cta = useMemo(
    () => ({
      label: t('cta'),
      onClick: onCtaClick,
    }),
    [t, onCtaClick],
  );

  const onClose = useCallback(() => {
    setIsDismissed(true);
    setBanner(BANNER_KEY, null);
  }, [setBanner]);

  useEffect(() => {
    if (!isTrialExpiring || isDismissed) {
      setBanner(BANNER_KEY, null);
      return;
    }

    const { type, expiringDate, deltaOfDays } = isTrialExpiring;

    const message =
      type === 'info' ? t('messageInfo', { expiringDate }) : t('messageWarning', { deltaOfDays });

    const iconName = type === 'info' ? 'lightBulb' : 'warning';

    setBanner(
      BANNER_KEY,
      <Banner
        message={message}
        cta={cta}
        type={type === 'info' ? 'primary-info' : type}
        iconName={iconName}
        onClose={onClose}
      />,
    );
  }, [isTrialExpiring, isDismissed, cta, onClose, t, setBanner]);

  return null;
};
